import React, { useState, useEffect, memo } from "react";
import { useTranslation } from "react-i18next";
import "../../services/languageconvertor";

// Import icons and images
import Icon from "../../media/icon/icons.jsx";
import Company_Logo_short from "../../media/image/Axipays_logo.webp";
import Contact_circularlines from "../../media/image/footer-img.png";
import visa from "../../media/image/visa.webp";
import mastercard from "../../media/image/mastercard.webp";
import unionpay from "../../media/image/unionpay.webp";
import certificates from "../../media/image/certificates.png";
import jcb from "../../media/image/jcb.webp";
import discover from "../../media/image/discover.webp";

// Language flags
import Fr from "../../media/image/flag/france.png";
import De from "../../media/image/flag/germany.png";
import Jp from "../../media/image/flag/japan.png";
import Es from "../../media/image/flag/spain.png";
import En from "../../media/image/flag/us.png";

import FooterModal from "../../components/Modal.jsx";

const Footer = ({ hideFooterTop = false }) => {
	const { t, i18n } = useTranslation();
	const [selectedLanguage, setSelectedLanguage] = useState("en");
	const [email, setEmail] = useState("");
	const [isSubscribed,] = useState(false);
	const [error,] = useState("");
	const [isVisible, setIsVisible] = useState(false);
	const [modalOpen, setModalOpen] = useState(false);
	const [modalTitle, setModalTitle] = useState("");
	const [modalContent, setModalContent] = useState("");

	const openPolicyModal = (type) => {
		let title = "";
		let content = "";

		switch (type) {
			case "terms":
				title = t("footer.termscondition");
				content = t("footer.termscontent");
				break;
			case "privacy":
				title = t("footer.privacypolicy");
				content = t("footer.privacycontent");
				break;
			case "cookies":
				title = t("footer.cookies");
				content = t("footer.cookiescontent");
				break;
			default:
				break;
		}

		setModalTitle(title);
		setModalContent(content);
		setModalOpen(true);
	};


	const handleSubscribe = async (e) => {
		e.preventDefault();

		const requestBody = {
			email: email,
		};

		try {
			const response = await fetch("https://api.axipays.com/api/v1/website/contact?subscribe=true", {
				method: "POST",
				headers: {
					"Content-Type": "application/json",
				},
				body: JSON.stringify(requestBody),
			});

			if (response.ok) {
				alert("Subscription Successful!");
				setEmail("");
			} else {
				alert("Failed to subscribe!");
			}
		} catch (error) {
			alert("Something went wrong. Please try again later.");
		}
	};

	// Language options for selection
	const languages = [
		{ code: "en", name: "English", flag: En },
		{ code: "es", name: "Español", flag: Es },
		{ code: "fr", name: "Français", flag: Fr },
		{ code: "de", name: "Deutsch", flag: De },
		{ code: "jp", name: "日本語", flag: Jp },
	];

	const toggleVisibility = () => {
		if (window.pageYOffset > 100) {
			setIsVisible(true);
		} else {
			setIsVisible(false);
		}
	};

	useEffect(() => {
		window.addEventListener('scroll', toggleVisibility);

		return () => {
			window.removeEventListener('scroll', toggleVisibility);
		};
	}, []);

	// Handle language change
	const handleLanguageChange = (event) => {
		const selectedLang = event.target.value;
		setSelectedLanguage(selectedLang);
		i18n.changeLanguage(selectedLang);
	};

	return (
		<section id="footer">
			<FooterModal
				isOpen={modalOpen}
				onClose={() => setModalOpen(false)}
				title={modalTitle}
				content={modalContent}
			/>
			{!hideFooterTop && (
				<div className="footer-top-part">
					<div className="footer-top-part-head">
						<img src={Contact_circularlines} alt="contact circular lines" />
						<h1>{t("footer.title")}</h1>
						<p className="contactMSG">{t("footer.subscribeText")}</p>
						<div className="footer-subscribe-btn">
							<input
								type="text"
								placeholder={t("footer.workEmail")}
								value={email}
								onChange={(e) => setEmail(e.target.value)}
							/>
							<button onClick={handleSubscribe}>
								{t("footer.subscribeButton")}
								<Icon name="right_arrow" color="#fff" />
							</button>
						</div>
						<div className="footer-subscribe-msg">
							<p className={`message ${isSubscribed ? "visible" : ""}`} style={{ color: "green" }}>
								{t("footer.subscribeMessage")}
							</p>
							<p className={`message ${error ? "visible" : ""}`} style={{ color: "red" }}>
								{t("footer.subscribeError")}
							</p>
						</div>
					</div>
				</div>
			)}
			<footer className="axipays-footer">
				<div className="footer-container">
					<div className="footer-header">
						<div className="logo-section">
							<img className="company_logo" src={Company_Logo_short} alt="Axipays brand logo" />
							<div className="language-convertor">
								<img src={languages.find((lang) => lang.code === selectedLanguage)?.flag} alt="Flag Icon" />
								<div className="divider"></div>
								<select
									value={selectedLanguage}
									onChange={handleLanguageChange}
									className="language-selector-select"
								>
									{languages.map((language) => (
										<option key={language.code} value={language.code}>
											{language.name}
										</option>
									))}
								</select>
							</div>

						</div>
						<div className="footer-p">
							<p>
								{t("footer.companyInfo")}
							</p>
							<div className="logo-line"></div>
						</div>
					</div>

					<div className="footer-links">
						<div className="footer-column">
							<h4>{t("footer.followUs")}</h4>
							<div className="footer-social-content footer-social-content-icons">
								<div>
									<a href="http://t.me/Axipays" target="_blank" rel="noreferrer" className="telegram-link">
										<Icon className="ic" width={20} height={20} name="telegramicon" color="#2563EB" />
									</a>
								</div>
								<div className="divider">.</div>
								<div>
									<a href="https://www.linkedin.com/company/axipays" rel="noreferrer" target="_blank" className="linkedin-link">
										<Icon className="ic" width={20} height={20} name="linkdinicon" color="#2563EB" />
									</a>
								</div>
							</div>
							<h4>{t("footer.emailUs")}</h4>
							<div className="footer-social-content">
								<Icon width={24} height={24} name="mail" />
								<a href="mailto:info@axipays.com"><p>info@axipays.com</p></a>
							</div>
							<h4>{t("footer.creditCards")}</h4>
							<div className="footer-social-content footer-cards">
								<div>
									<img src={visa} alt="Visa" />
									<img src={mastercard} alt="Mastercard" />
									<img src={unionpay} alt="Unionpay" />
									<img src={discover} alt="Discover" />
									<img src={jcb} alt="Jcb" />
								</div>
							</div>
						</div>

						<div className="footer-column">
							<ul>

								<h3>{t("footer.aboutUs")}</h3>
								<a href="#landingpage"><li>{t("footer.home")}</li></a>
								<a href="#services"><li>{t("footer.services")}</li></a>
								<a href="#features"><li>{t("footer.features")}</li></a>
								<a href="#contact"><li>{t("footer.contact")}</li></a>
							</ul>

						</div>

						<div className="footer-column">
							<ul>
								<h3>{t("footer.services")}</h3>
								<a href="#section2"><li>{t("footer.creditCardProcessing")}</li></a>
								<a href="#section2"><li>{t("footer.alternatePayments")}</li></a>
								<a href="#section2"><li>{t("footer.whiteLabel")}</li></a>
								<a href="#section2"><li>{t("footer.cryptoProcessing")}</li></a>
								<a href="#section2"><li>{t("footer.integration")}</li></a>

							</ul>
						</div>

						<div className="footer-column">
							<ul>
								<h3>{t("footer.termspolicy")}</h3>
								<li onClick={() => openPolicyModal("terms")}>{t("footer.termscondition")}</li>
								<li onClick={() => openPolicyModal("privacy")}>{t("footer.privacypolicy")}</li>
								<li onClick={() => openPolicyModal("cookies")}>{t("footer.cookies")}</li>
							</ul>
						</div>

						<div className="footer-column">
							<ul>

								<h3>{t("footer.openSource")}</h3>
								<li><a href="https://developers.axipays.com/" target="blank">{t("footer.apiDocs")}</a></li>
							</ul>
						</div>

					</div>
					<div className="certificates" >
						<img src={certificates} alt="certificates" />
					</div>
					<div className="bottom-tag-line">
						<div className="bottom-tag-top">
							<div className="bottom-line bottom-line-color"></div>
							{t("footer.tagline")}
							<div className="bottom-line bottom-line-color"></div>
							{isVisible && (
								<div
									className="bottom-tag-arrow"
									onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
								>
									<Icon name="upward_arrow" width={25} height={25} color="#2563EB" />
								</div>
							)}
						</div>
						<p>© 2025 AXIPAYS. All rights reserved.</p>

					</div>
				</div>

			</footer>

		</section>
	);
};
export default memo(Footer);